import { Info } from "lucide-react";
import { useRef, useState } from "react";
import { createPortal } from "react-dom";

import { cn } from "../../lib/cn.js";

/** Tooltip box width (`w-64` = 256px). */
const TOOLTIP_WIDTH = 256;

export function InfoTooltip({
  text,
  className,
}: {
  readonly text: string;
  readonly className?: string;
}): React.JSX.Element {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [coords, setCoords] = useState<{
    top: number;
    left: number;
    arrowLeft: number;
    placement: "above" | "below";
  } | null>(null);

  const showTooltip = (): void => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect === undefined) return;

    const triggerCenter = rect.left + rect.width / 2;
    const placement = window.innerHeight - rect.bottom < 100 ? "above" : "below";

    // Keep tooltip at least 16px from left and 24px from right (avoiding scrollbar crowding)
    const minLeft = 16;
    const maxLeft = Math.max(minLeft, window.innerWidth - TOOLTIP_WIDTH - 24);
    const desiredLeft = triggerCenter - TOOLTIP_WIDTH / 2;
    const left = Math.min(Math.max(desiredLeft, minLeft), maxLeft);

    // Arrow points directly at the trigger center relative to the tooltip box
    const arrowLeft = Math.min(Math.max(triggerCenter - left, 14), TOOLTIP_WIDTH - 14);

    setCoords({
      top: placement === "below" ? rect.bottom + 8 : rect.top - 8,
      left,
      arrowLeft,
      placement,
    });
  };

  const hideTooltip = (): void => setCoords(null);

  return (
    <span className={cn("inline-flex items-center", className)}>
      <button
        ref={triggerRef}
        type="button"
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
        aria-label="More info"
        className="text-muted-foreground hover:text-foreground inline-flex items-center cursor-pointer transition-colors"
      >
        <Info className="size-3.5" />
      </button>

      {coords !== null &&
        createPortal(
          <span
            role="tooltip"
            style={{
              position: "fixed",
              top: coords.top,
              left: coords.left,
              transform: coords.placement === "above" ? "translateY(-100%)" : undefined,
            }}
            className="pointer-events-none z-50 w-64 rounded-md bg-[#262626] px-2.5 py-1.5 text-xs font-normal whitespace-normal text-white shadow-lg"
          >
            <span
              aria-hidden="true"
              style={{ left: coords.arrowLeft }}
              className={cn(
                "absolute size-2 -translate-x-1/2 rotate-45 bg-[#262626]",
                coords.placement === "below" && "top-0 -translate-y-1/2",
                coords.placement === "above" && "bottom-0 translate-y-1/2",
              )}
            />
            {text}
          </span>,
          document.body,
        )}
    </span>
  );
}
